import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, MenuItem, FormControl, InputLabel, Select, Snackbar, Alert, Chip, OutlinedInput } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TaskDraftDTO, TaskDraftStatus } from '../../../../types/TaskDraftDTO';
import { taskService } from '../../../../services/taskService';
import { mediaService } from '../../../../services/mediaService';
import { useAuth } from '../../../../contexts/AuthContext';
import { categoryService } from '../../../../services/categoryService';
import { Category } from '../../../../types/category';
import { TaskDraftCategory } from '../../../../types/TaskDraftCategory';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const CreateTaskBriefPage: React.FC = () => {
    const navigate = useNavigate();
    const { organizationId } = useParams<{ organizationId: string }>(); // Получаем organizationId из URL
    const { state } = useAuth();
    const authenticatedUser = state.user;

    const [taskName, setTaskName] = useState('');
    const [budget, setBudget] = useState('');
    const [description, setDescription] = useState('');
    const [requirements, setRequirements] = useState('');
    const [numberOfExecutors, setNumberOfExecutors] = useState('');
    const [deadline, setDeadline] = useState<Date | null>(null);
    const [contactMethod, setContactMethod] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categoriesResponse = await categoryService.getAllCategories();
                setAvailableCategories(categoriesResponse.data);
            } catch (error) {
                console.error('Error fetching categories:', error);
                setSnackbarMessage('Ошибка при загрузке категорий.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        };

        fetchCategories();
    }, []);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!organizationId) {
            setSnackbarMessage('Ошибка: ID организации не определен.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        if (!authenticatedUser?.manager?.id) {
            setSnackbarMessage('Ошибка: Менеджер не авторизован.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        const newTaskDraft: TaskDraftDTO = {
            title: taskName,
            description: description,
            priceInKopecks: budget ? parseFloat(budget) * 100 : undefined,
            deadline: deadline ? deadline.toISOString() : undefined,
            requirements: requirements,
            neededUsers: numberOfExecutors ? parseInt(numberOfExecutors) : undefined,
            organizationId: organizationId, // Используем organizationId из useParams
            managerId: authenticatedUser.manager.id,
            status: TaskDraftStatus.IN_PROCESS, // Default status for a new brief
            taskDraftCategories: selectedCategories.map(c => ({ categoryId: c.id!, category: c as Category } as TaskDraftCategory)),
        };

        try {
            const createdTaskDraft = await taskService.createTaskDraft(newTaskDraft);

            if (selectedFile && createdTaskDraft.id) {
                await mediaService.uploadFile(selectedFile, createdTaskDraft.id);
            }

            setSnackbarMessage('Бриф задачи успешно создан!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            setTimeout(() => {
                navigate(`/admin/organizations/${organizationId}`); // Используем organizationId из useParams
            }, 2000); // Redirect after 2 seconds

        } catch (error) {
            console.error('Error creating task brief:', error);
            setSnackbarMessage('Ошибка при создании брифа задачи.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        } else {
            setSelectedFile(null);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 800, margin: 'auto' }}>
            <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 4 }}>
                Заполните бриф для задачи:
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <TextField
                    label="Название задачи **"
                    placeholder="Краткая формулировка в 2-3 словах"
                    value={taskName}
                    onChange={(e) => setTaskName(e.target.value)}
                    fullWidth
                    required
                    sx={{ gridColumn: 'span 1' }}
                    helperText="Пример: 'Проверка цен конкурентов'"
                />
                <TextField
                    label="Укажите бюджет"
                    placeholder="Сколько вы планируете заплатить за выполненные задачи"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    fullWidth
                    required
                    sx={{ gridColumn: 'span 1' }}
                    helperText="Можно указать фикс. стоимость, вилку или 'готовы обсудить стоимость'"
                />
                <TextField
                    label="Опишите задание"
                    placeholder="Подробное описание содержания и образа результата"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    required
                    multiline
                    rows={6} // Adjust rows as needed to match the mockup's height
                    sx={{ gridColumn: 'span 2', mb: 2 }}
                    helperText="Пример: &quot;Заполнить таблицу Excel с данными о 100 товарах: название, артикул,цена. Шаблон таблицы предоставляется&quot;"
                />
                
                <FormControl fullWidth required sx={{ gridColumn: 'span 1' }}>
                    <InputLabel id="categories-label">Категория задачи **</InputLabel>
                    <Select
                        labelId="categories-label"
                        multiple
                        value={selectedCategories.map(c => c.id)}
                        onChange={(event) => {
                            const value = event.target.value as string[];
                            setSelectedCategories(availableCategories.filter(c => value.includes(c.id!)));
                        }}
                        input={<OutlinedInput id="select-multiple-chip-category" label="Категория задачи **" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => {
                                    const category = availableCategories.find(c => c.id === value);
                                    return <Chip key={value} label={category?.title || value} />;
                                })}
                            </Box>
                        )}
                        MenuProps={MenuProps}
                    >
                        {availableCategories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                                {category.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Прикрепите ссылку на облачную папку или документ"
                    placeholder={selectedFile ? selectedFile.name : "Например: Google Диск, Яндекс.Документы"}
                    value={selectedFile ? selectedFile.name : ''}
                    onChange={(e) => { /* Removed cloudLink state */ }}
                    fullWidth
                    sx={{ gridColumn: 'span 1' }}
                    InputProps={{
                        endAdornment: (
                            <Button
                                variant="contained"
                                component="label"
                                size="small"
                            >
                                <span role="img" aria-label="attachment">📎</span>
                                <input type="file" hidden onChange={handleFileChange} />
                            </Button>
                        ),
                        startAdornment: (
                            <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                                {selectedFile ? "" : "файл.pdf"}
                            </Typography>
                        )
                    }}
                    helperText=""
                />
                <TextField
                    label="Укажите дополнительные требования/ограничения"
                    placeholder="Выберите из списка или укажите своё"
                    value={requirements}
                    onChange={(e) => setRequirements(e.target.value)}
                    fullWidth
                    sx={{ gridColumn: 'span 1' }}
                    helperText="Пример: 'Возраст исполнителя', 'дресс-код' и тп"
                />
                <TextField
                    label="Укажите количество исполнителей"
                    placeholder="Сколько исполнителей вам требуется"
                    value={numberOfExecutors}
                    onChange={(e) => setNumberOfExecutors(e.target.value)}
                    fullWidth
                    required
                    sx={{ gridColumn: 'span 1' }}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Укажите срок выполнения задачи *"
                        value={deadline}
                        onChange={(newValue) => setDeadline(newValue)}
                        slots={{ textField: (params) => <TextField {...params} fullWidth sx={{ gridColumn: 'span 1' }} helperText="Также можете указать даты этапов выполнения" /> }}
                    />
                </LocalizationProvider>
                <TextField
                    label="Укажите удобный способ связи"
                    placeholder="Если необходимо обсудить детали"
                    value={contactMethod}
                    onChange={(e) => setContactMethod(e.target.value)}
                    fullWidth
                    sx={{ gridColumn: 'span 1' }}
                    helperText="Можете указать несколько способов связи"
                />
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ gridColumn: 'span 2', mt: 3, py: 1.5, fontSize: '1rem' }}
                >
                    ОТПРАВИТЬ БРИФ
                </Button>
            </Box>
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CreateTaskBriefPage;
